import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  message, Modal, Table, Button, Dropdown, Menu,
  Pagination, ConfigProvider, Tag, Tooltip, DatePicker,
  Input,
  Flex,
  Skeleton,
  Alert,
} from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { MakeApiCall } from "../../../apis";
import { DefaultAdminPage, DefaultUserPage, primaryColor, Types } from "../../../config";
import { fakeImage } from "../../../config/fakeData";
import UserModal from "./user-config";
import { Helmet } from "react-helmet";
import { debounce } from "lodash";
import { Wrapper } from "./style";

const findRoutes = {
  superAdmin: DefaultAdminPage,
  admin: DefaultAdminPage,
  manager: DefaultAdminPage,
  user: DefaultUserPage,
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function DropdownUserList(props) {
  const { setData, userData } = props;

  {console.log(userData)}

  const navigate = useNavigate();

  const [totalPage, setTotalPage] = useState(0);
  const [pageSize,  setPageSize]  = useState(10);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [list,      setList]      = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortParam, setSortParam] = useState(""); // New sorting state

  const [savingRowId, setSavingRowId] = useState(null);

  // ---- New: keep a ref to action dropdown being open, to prevent row click switch
  const actionDropdownOpenRow = useRef(null);

  const getSortOrder = (columnKey) => {
    if (sortParam === columnKey) return 'ascend';
    if (sortParam === `-${columnKey}`) return 'descend';
    return null;
  };

  const getUserList = async (data) => {
    setLoading(true);
    let url = `parent-user/child-users?search_text=${data?.searchText ?? searchText}&page=${data?.page || page || 1}&per-page=${data?.pageSize || pageSize}`;
    const currentSort = data?.sortParam !== undefined ? data.sortParam : sortParam;
    if (currentSort) {
      url += `&sort=${currentSort}`;
    }
    const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);
    if (response?.status) {
      setTotalPage(response?.data?.pagination?.totalCount || 0);
      setPage(response?.data?.pagination?.page || 1);
      setList(response?.data?.records || []);
    } else {
      message.warning(response?.message);
      setList([]);
    }
    setLoading(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    let newSortValue = "";

    if (activeSorter.order) {
      const field = activeSorter.field || activeSorter.columnKey;
      newSortValue = activeSorter.order === "ascend" ? field : `-${field}`;
    }

    setSortParam(newSortValue);
    setPage(1);
    getUserList({ page: 1, sortParam: newSortValue });
  };

  const DeleteUser = async (id) => {
    message.destroy();
    message.loading("Loading...", 0);
    const response = await MakeApiCall(`user/${id}`, "delete", null, true, {}, userData?.token);
    if (response?.status) {
      getUserList();
      message.destroy();
      message.success(response?.message);
    } else {
      message.destroy();
      message.warning(response?.message);
    }
  };

  useEffect(() => {
    getUserList();
  }, [page, pageSize, searchText]);

  const SwitchAccount = async (id) => {
    const response = await MakeApiCall(`switch-user`, "post", { id }, true, {}, userData?.token);
    if (response?.status === true) {
      const type = Types?.[response?.data?.user_data?.user_type];
      // Preserve original admin context once created, do not overwrite with child context.
      const adminContext = userData?.admin || userData;
      setData({ admin: adminContext, user: response?.data, loginType: type, token: response?.data?.auth_token_data?.access_token });
      navigate(findRoutes?.[type]);
    } else {
      message.warning(response?.message);
    }
  };

  const [visible,      setVisible]      = useState(false);
  const [editingUser,  setEditingUser]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleCancel = () => { setVisible(false); setEditingUser(null); };

  const CreateUser = async (data) => {
    message.destroy(); message.loading("Loading...", 0);
    const response = await MakeApiCall(`parent-user/child-users`, "post", { ...data }, true, {}, userData?.token);
    if (response?.status === true) { handleCancel(); getUserList(); message.destroy(); message.success(response?.message || "Successfully created"); }
    else { message.destroy(); message.warning(response?.message || "Failed to create"); }
  };

  const UpdateUser = async (data) => {
    if (!editingUser?.id) return;
    message.destroy(); message.loading("Loading...", 0);
    const response = await MakeApiCall(`parent-user/child-users/${editingUser?.id}`, "put", data, true, {}, userData?.token);
    if (response?.status === true) { handleCancel(); getUserList(); message.destroy(); message.success(response?.message || "Successfully updated"); }
    else { message.destroy(); message.warning(response?.message || "Failed to update"); }
  };

  const handleSubmit = (values) => { if (editingUser) { UpdateUser(values); } else { CreateUser(values); } setVisible(false); setEditingUser(null); };
  const handleEdit   = (user) => { setEditingUser(user); setVisible(true); };

  const handleSearch1 = (e)    => { setSearchText(e.target.value); setPage(1); };

  const onSearch = debounce((value) => {
    setSearchText(value); 
    setPage(1);
  }, 400);

  const isRestricted = userData?.user?.user_data?.user_type === 3 || userData?.user?.user_data?.user_type === 4;

  // Find current user id from userData
  const currentUserId = userData?.user?.user_data?.id;

  // ---- new: remove Switch User column, add row click handler
  // Pass the rowKey to the Action column to help track open dropdown
  const columns1 = [
    { title: "#", key: "index", render: (_, __, index) => (page - 1) * pageSize + index + 1 },
    {
      title: "User Details", dataIndex: "name", key: "name", sorter: true, sortOrder: getSortOrder('name'),
      render: (_, record) => (
          <div className="d-flex align-items-center">
            <div className="symbol symbol-circle symbol-50px overflow-hidden me-3"><img src={record?.photo || fakeImage} alt="" /></div>
            <div className="d-flex flex-column"><Link href className="text-gray-800 text-hover-danger mb-1 fw-bolder">{record.name || ""}</Link><span>{record.email || ""}</span></div>
          </div>
      ),
    },
    { title: "Seller Name", dataIndex: "seller_name", key: "seller_name", sorter: true, sortOrder: getSortOrder('seller_name'), render: (text) => <span>{text || "-"}</span> },
    {
      title: "Seller Central Status", dataIndex: "is_amz_sp_api_connected", key: "is_amz_sp_api_connected", align: "center", sorter: true, sortOrder: getSortOrder('is_amz_sp_api_connected'),
      render: (val) => val === 1 ? <Tag color="success">Connected</Tag> : <Tag color="error">Not Connected</Tag>,
    },
    {
      title: "Ads Account Status", dataIndex: "is_amz_ads_connected", key: "is_amz_ads_connected", align: "center", sorter: true, sortOrder: getSortOrder('is_amz_ads_connected'),
      render: (val) => val === 1 ? <Tag color="success">Connected</Tag> : <Tag color="error">Not Connected</Tag>,
    },
    {
      title: "Marketplace",
      dataIndex: "marketplace",
      key: "marketplace",
      align: "center",
      render: (val, record) => {
        const countryCode = record?.marketplace_country_code || "A21TJRUUN4KGV";
        const marketplace = record?.marketplace || "Amazon.in";
        const flagUrl = `/assets/media/domainImage/${countryCode.toLowerCase()}.png`;
        return (
          <span>
            <img
              src={flagUrl}
              alt={countryCode}
              title={marketplace}
              style={{ width: 24, height: 24, marginRight: 8, verticalAlign: 'middle', borderRadius: 2,}}
              onError={e => {e.target.onerror=null; e.target.style.display='none'}}
            />
            {marketplace}
          </span>
        );
      }
    },
    { title: "Created at", dataIndex: "created_at", key: "created_at", render: (timestamp) => new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).format(new Date(timestamp * 1000)) },
    {
      title: "Action", key: "action", fixed: 'right',
      render: (_, row) => (
          <Dropdown
            overlay={menu(row, isRestricted)}
            trigger={["click"]}
            disabled={isRestricted}
            onOpenChange={open => {
              // Set ref when dropdown for a row is opened, clear when closed
              if (open) {
                actionDropdownOpenRow.current = row.id;
              } else if (actionDropdownOpenRow.current === row.id) {
                actionDropdownOpenRow.current = null;
              }
            }}
          >
            <Button type="link" disabled={isRestricted}>
              <i className={`ki-solid ki-dots-vertical fs-1 ${isRestricted ? "text-gray-400" : "text-gray-800"}`} />
            </Button>
          </Dropdown>
      ),
    },
  ].filter(col => !(isRestricted && col.key === "action"));

  const menu = (row, isRestricted = false) => (
      <Menu className="min-w-200px p-3">
        <Menu.Item key="1" className="d-flex align-items-center mb-2" disabled={isRestricted} onClick={() => !isRestricted && handleEdit(row)}>
          <i className="ki-outline ki-pencil fs-3 me-2" />Edit user
        </Menu.Item>
        <Menu.Item key="2" className="d-flex align-items-center" disabled={isRestricted} onClick={() => !isRestricted && setDeleteTarget(row)}>
          <i className="ki-outline ki-trash fs-3 me-2 text-danger" /><span className="text-danger">Delete user</span>
        </Menu.Item>
      </Menu>
  );

  const theme = {
    token: {
      colorPrimary: "#f45b68",
      // ... (other tokens remain unchanged)
    },
  };

  // Find the id of the currently logged in user for the dropdown. Support fallbacks.
  const currentIdForDisable = userData?.user?.user_data?.id;

  return (
      <Wrapper>
        <div style={{ margin: 15 }}>
          <div>
            <div style={{ marginBottom: 12, fontWeight: 600 }}>Select Seller Account</div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                rowGap: 5,
                maxHeight: 320,
                overflowY: "auto"
              }}
            >
              {loading ? (
                <Skeleton paragraph={{ rows: 4 }} />
              ) : (
                list.map((user) => {
                  const isCurrent = user.id === currentIdForDisable;
                  return (
                    <Button
                      key={user.id}
                      type={isCurrent ? "primary" : "default"}
                      disabled={isRestricted || isCurrent}
                      onClick={() => {
                        if (!isRestricted && !isCurrent) {
                          SwitchAccount(user.id);
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        padding: 8,
                        gap: 5,
                        borderRadius: 6,
                        border: 0,
                        // border: `1px solid ${isCurrent ? primaryColor : "#fff"}`,
                        background: isCurrent ? "#f9ecec" : undefined,
                        // fontWeight: isCurrent ? 700 : 500,
                        color: isCurrent ? primaryColor : undefined,
                        opacity: isCurrent ? 1 : undefined
                      }}
                      block
                    >
                      <img
                        src={user.photo || fakeImage}
                        alt={user.name}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginRight: 8,
                          flexShrink: 0,
                          // border: isCurrent ? "2px solid #0958d9" : undefined
                        }}
                      />
                      <span style={{ fontWeight: isCurrent ? 700 : 500, fontSize: 12, flexGrow: 1 }}>
                        {user.name}
                        {/* {isCurrent && (
                          <span style={{ fontSize: 11, color: "#219653", marginLeft: 8, fontWeight: 600 }}>(Current)</span>
                        )} */}
                      </span>
                      {/* {user.is_parent && (
                        <Tag color="blue" style={{ marginRight: 0 }}>
                          P
                        </Tag>
                      )} */}
                      <div className="">
                      <Button
                        size="small"
                        type="text"
                        title="Delete this account"
                        style={{ marginLeft: 4, display: "flex", alignItems: "center" }}
                        disabled={isRestricted || user.is_parent}
                   
                        onClick={(e) => {
                          e.stopPropagation(); // 👈 This prevents event from bubbling to parent
                          if (!isRestricted) setDeleteTarget(user);
                        }}
                      >
                        <i className="ki-outline ki-trash text-danger" style={{ fontSize: 16 }} />
                      </Button>
                 
                      </div>
                    </Button>
                   
                  );
                })
              )}
            </div>
            <Button
              type="link"
              danger
              className="mt-2 fs-6 px-2 fw-bold "
              onClick={() => setVisible(true)}
              icon={<i className="ki-outline ki-plus fs-2 me-0 text-danger" />}
              style={{ display: "flex", alignItems: "center" }}
            >
              Create New Account
            </Button>
          </div>
        </div>
  
  
{/* 
        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed">
                <div className="d-flex flex-column mt-3">
                  <h3 className="card-title fw-bolder">Seller Accounts</h3>
                  <div style={{fontSize: "13px", color:  primaryColor, marginTop: "0px"}}><i>Click on the account to switch seller.</i></div>
                </div>

                <div className="card-toolbar ">
                  <button
                    className="px-5 btn btn-danger fs-6 px-5 fw-bold"
                    onClick={() => setVisible(true)}
                  >
                    Create New Account
                  </button>
                </div>
              </div>
              <div className="card-body py-2">
                <div className="table-responsive">
                  <Table
                      scroll={{ x: "max-content" }}
                      columns={columns1}
                      loading={loading}
                      dataSource={list}
                      rowKey="id"
                      pagination={false}
                      onChange={handleTableChange} // Added sorting handler
                      onRow={onRowHandler}
                      components={{
                        table: (props) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />,
                        header: {
                          wrapper: (props) => <thead {...props} />,
                          row:     (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                          cell:    (props) => <th {...props} />,
                        },
                      }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-body py-2">
                <ConfigProvider theme={theme}>
                  <Pagination
                      className="mt-3"
                      current={page}
                      pageSize={pageSize}
                      total={totalPage}
                      showSizeChanger
                      onChange={(currentPage, newPageSize) => {
                        setPage(currentPage);
                        setPageSize(newPageSize);
                      }}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </ConfigProvider>
              </div>
            </div>
          </div>
        </div>
*/}
        <UserModal visible={visible} onCancel={handleCancel} onSubmit={handleSubmit} initialValues={editingUser} />

        <Modal open={!!deleteTarget} onCancel={() => setDeleteTarget(null)} footer={null} centered width={420}>
          <div className="text-center px-2 py-4">
            <div className="mb-4" style={{ fontSize: 48, lineHeight: 1 }}>
            <i class="ki-duotone ki-trash text-danger" style={{ fontSize: 48, lineHeight: 1 }}>
              <span class="path1"></span>
              <span class="path2"></span>
              <span class="path3"></span>
              <span class="path4"></span>
              <span class="path5"></span>
            </i>
            </div>
            <h4 className="fw-bolder text-gray-800 mb-2">Delete User?</h4>
            <p className="text-muted mb-6">You are about to delete <span className="fw-bold text-gray-800">{deleteTarget?.name}</span>.</p>
            <div className="alert alert-danger mb-4 fs-7 px-3 py-3" style={{borderColor: '#ffccc7'}}>
              This action cannot be undone. All data associated with this user will be permanently deleted and cannot be recovered.
            </div>
      
            <div className="d-flex justify-content-center gap-3">
              <Button size="large" color="default" variant="filled" className="fs-6" onClick={() => setDeleteTarget(null)} style={{ minWidth: 110 }}>Cancel</Button>
              <Button danger type="primary" className="fs-6" size="large" style={{ minWidth: 110 }} onClick={() => { DeleteUser(deleteTarget?.id); setDeleteTarget(null); }}>Yes, Delete</Button>
            </div>
          </div>
        </Modal> 
      </Wrapper>
  );
}